/**
 * Order service.
 * Integrated with backend API.
 */

import { apiClient } from './apiClient';

const API_BASE_URL = '/api/v1';

/**
 * Create a new order.
 * @param {Object} payload - order header fields + items: [{ productUid, cartons, orderQty, price, ... }]
 */
export const createOrder = async (payload) => {
  try {
    const response = await apiClient.post(`${API_BASE_URL}/orders`, payload);
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error('Failed to create order:', error);
    throw error;
  }
};

/**
 * Update an existing order (also resets it to pending authorization on the backend).
 */
export const updateOrder = async (id, payload) => {
  try {
    const response = await apiClient.patch(`${API_BASE_URL}/orders/${id}`, payload);
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error('Failed to update order:', error);
    throw error;
  }
};

/**
 * Authorize / un-authorize an order.
 * @param {number|string} id
 * @param {'authorized'|'pending'} status
 */
export const updateOrderAuthorization = async (id, status) => {
  try {
    const response = await apiClient.patch(`${API_BASE_URL}/orders/${id}/authorization`, { status });
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error('Failed to update order authorization:', error);
    throw error;
  }
};

/**
 * List recent orders as a plain array (used by the authorization grid).
 * Requests a high per_page so the caller receives the full working set, not just one page.
 */
export const getOrders = async () => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/orders`, { params: { per_page: 200 } });
    return response.data?.data ?? [];
  } catch (error) {
    console.error('Failed to fetch orders:', error);
    return [];
  }
};

/**
 * List orders with server-side pagination and filtering.
 * @param {Object} opts
 * @param {number} [opts.page=1]
 * @param {number} [opts.perPage=30]
 * @param {string} [opts.search='']    matches order no / buyer PO / buyer / country / merchant / status
 * @param {string} [opts.fromDate='']  inclusive lower bound on the order received date (YYYY-MM-DD)
 * @param {string} [opts.toDate='']    inclusive upper bound on the order received date (YYYY-MM-DD)
 * @returns {Promise<{ data: Array, pagination: { total:number, page:number, per_page:number, last_page:number } }>}
 */
export const getOrdersPaged = async ({ page = 1, perPage = 30, search = '', fromDate = '', toDate = '' } = {}) => {
  const params = { page, per_page: perPage };
  if (search) params.search = search;
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;

  try {
    const response = await apiClient.get(`${API_BASE_URL}/orders`, { params });
    return {
      data: Array.isArray(response.data?.data) ? response.data.data : [],
      pagination: response.data?.pagination ?? { total: 0, page: 1, per_page: perPage, last_page: 1 },
    };
  } catch (error) {
    console.error('Failed to fetch orders:', error);
    return { data: [], pagination: { total: 0, page: 1, per_page: perPage, last_page: 1 } };
  }
};

/**
 * Fetch a single order with its items.
 */
export const getOrder = async (id) => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/orders/${id}`);
    return response.data?.data ?? null;
  } catch (error) {
    console.error('Failed to fetch order:', error);
    throw error;
  }
};
